import React, { useState } from "react";
import {
  CreditCard,
  ShoppingCart,
  Plus,
  Minus,
  Wallet,
  Clock,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import "./CafeteriaPurchase.css";

const CafeteriaPurchase = () => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [studentBalance] = useState(150.0); // Mock balance

  // Mock menu items
  const menuItems = [
    {
      id: 1,
      name: "Chicken Burger",
      price: 8.5,
      category: "Main Course",
      description: "Grilled chicken with fresh vegetables",
      image: "🍔",
      available: true,
    },
    {
      id: 2,
      name: "Caesar Salad",
      price: 6.0,
      category: "Salad",
      description: "Fresh lettuce with caesar dressing",
      image: "🥗",
      available: true,
    },
    {
      id: 3,
      name: "Pizza Slice",
      price: 4.5,
      category: "Main Course",
      description: "Cheese pizza slice",
      image: "🍕",
      available: true,
    },
    {
      id: 4,
      name: "Fresh Juice",
      price: 3.0,
      category: "Beverage",
      description: "Orange or apple juice",
      image: "🥤",
      available: true,
    },
    {
      id: 5,
      name: "Chocolate Cake",
      price: 5.0,
      category: "Dessert",
      description: "Rich chocolate cake slice",
      image: "🍰",
      available: false,
    },
    {
      id: 6,
      name: "Sandwich",
      price: 7.0,
      category: "Main Course",
      description: "Turkey and cheese sandwich",
      image: "🥪",
      available: true,
    },
  ];

  const categories = ["All", "Main Course", "Salad", "Beverage", "Dessert"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredItems = menuItems.filter(
    (item) => selectedCategory === "All" || item.category === selectedCategory
  );

  const addToCart = (item) => {
    if (!item.available) {
      toast.error("This item is currently unavailable");
      return;
    }

    const existingItem = cart.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      setCart(
        cart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter((item) => item.id !== itemId));
    toast.success("Item removed from cart");
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(
      cart.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setShowPaymentModal(true);
  };

  const processPayment = () => {
    const total = getTotalPrice();
    if (total > studentBalance) {
      toast.error("Insufficient balance. Please top up your account.");
      return;
    }

    // Simulate payment processing
    toast.success(
      `Payment successful! $${total.toFixed(2)} deducted from your account.`
    );
    setCart([]);
    setShowPaymentModal(false);
  };

  return (
    <div className="cafeteria-purchase">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <CreditCard size={24} />
          </div>
          <div>
            <h1>Cafeteria Purchase</h1>
            <p>Order food and beverages from the school cafeteria</p>
          </div>
        </div>
        <div className="balance-info">
          <div className="balance-card">
            <Wallet size={20} />
            <div>
              <span className="balance-label">Available Balance</span>
              <span className="balance-amount">
                ${studentBalance.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="purchase-content">
        <div className="main-content">
          {/* Category Filter */}
          <div className="category-filter">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-btn ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Menu Items */}
          <div className="menu-section">
            <h2>Menu Items</h2>
            <div className="menu-grid">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={`menu-item ${
                    !item.available ? "unavailable" : ""
                  }`}
                >
                  <div className="item-image">{item.image}</div>
                  <div className="item-info">
                    <h3>{item.name}</h3>
                    <p className="item-description">{item.description}</p>
                    <div className="item-meta">
                      <span className="item-category">{item.category}</span>
                      <span className="item-price">
                        ${item.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    className="add-to-cart-btn"
                    onClick={() => addToCart(item)}
                    disabled={!item.available}
                  >
                    <Plus size={16} />
                    {!item.available ? "Unavailable" : "Add to Cart"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Shopping Cart */}
        <div className="cart-sidebar">
          <div className="cart-header">
            <ShoppingCart size={20} />
            <h3>Shopping Cart</h3>
            <span className="cart-count">{cart.length}</span>
          </div>

          {cart.length === 0 ? (
            <div className="empty-cart">
              <p>Your cart is empty</p>
              <p>Add items from the menu to get started</p>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {cart.map((item) => (
                  <div key={item.id} className="cart-item">
                    <div className="item-details">
                      <span className="item-name">{item.name}</span>
                      <span className="item-price">
                        ${item.price.toFixed(2)}
                      </span>
                    </div>
                    <div className="quantity-controls">
                      <button
                        className="quantity-btn"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity - 1)
                        }
                      >
                        <Minus size={14} />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button
                        className="quantity-btn"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity + 1)
                        }
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => removeFromCart(item.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="cart-footer">
                <div className="total-section">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>${getTotalPrice().toFixed(2)}</span>
                  </div>
                  <div className="total-row">
                    <span>Tax (10%):</span>
                    <span>${(getTotalPrice() * 0.1).toFixed(2)}</span>
                  </div>
                  <div className="total-row total">
                    <span>Total:</span>
                    <span>${(getTotalPrice() * 1.1).toFixed(2)}</span>
                  </div>
                </div>
                <button className="checkout-btn" onClick={handleCheckout}>
                  <CreditCard size={16} />
                  Checkout
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Payment</h3>
              <button
                className="modal-close"
                onClick={() => setShowPaymentModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="payment-summary">
                <h4>Order Summary</h4>
                {cart.map((item) => (
                  <div key={item.id} className="payment-item">
                    <span>
                      {item.name} x {item.quantity}
                    </span>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="payment-total">
                  <span>Total: ${(getTotalPrice() * 1.1).toFixed(2)}</span>
                </div>
              </div>
              <div className="balance-check">
                <div className="current-balance">
                  <Wallet size={16} />
                  <span>Current Balance: ${studentBalance.toFixed(2)}</span>
                </div>
                {getTotalPrice() * 1.1 > studentBalance && (
                  <div className="insufficient-funds">
                    <Clock size={16} />
                    <span>Insufficient funds. Please top up your account.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={processPayment}
                disabled={getTotalPrice() * 1.1 > studentBalance}
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CafeteriaPurchase;
